import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { getThinkingAnimationText } from '../config/constants';
import { useChatLanguage } from '../context/ChatThemeContext';

interface ThinkingAnimationProps {
    isVisible: boolean;
}

const ThinkingAnimation: React.FC<ThinkingAnimationProps> = ({ isVisible }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [dotCount, setDotCount] = useState(1);

    // Get the effective language using the theme context
    const effectiveLanguage = useChatLanguage();

    const thinkingSteps = useMemo(
        () => [
            { text: getThinkingAnimationText('PLANNING', effectiveLanguage), duration: 2000 },
            { text: getThinkingAnimationText('PROCESSING', effectiveLanguage), duration: 2600 },
            { text: getThinkingAnimationText('CRAFTING', effectiveLanguage), duration: 2200 },
            { text: getThinkingAnimationText('GENERATING', effectiveLanguage), duration: 0 }, // Final step - stays here
        ],
        [effectiveLanguage]
    );

    // Go through thinking steps once, then stay on final step
    useEffect(() => {
        if (!isVisible) {
            setCurrentStep(0);
            return;
        }

        if (currentStep < thinkingSteps.length - 1) {
            const stepInterval = setTimeout(() => {
                setCurrentStep((prev) => prev + 1);
            }, thinkingSteps[currentStep]?.duration || 2000);

            return () => clearTimeout(stepInterval);
        }
    }, [isVisible, currentStep, thinkingSteps]);

    // Animate dots: . -> .. -> ... -> . (loop)
    useEffect(() => {
        if (!isVisible) {
            setDotCount(1);
            return;
        }

        const dotInterval = setInterval(() => {
            setDotCount((prev) => (prev % 3) + 1);
        }, 500);

        return () => clearInterval(dotInterval);
    }, [isVisible]);

    if (!isVisible) return null;

    const currentThinkingStep = thinkingSteps[currentStep];

    return (
        <div style={{ width: '100%' }}>
            <span
                style={{
                    fontSize: '15px',
                    fontWeight: 400,
                    color: '#9CA3AF',
                    lineHeight: '1.6',
                    animation: 'textFadeIn 0.4s ease-out',
                }}
            >
                {currentThinkingStep.text}
                {'.'.repeat(dotCount)}
            </span>
        </div>
    );
};

export default ThinkingAnimation;
